import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useApp } from "../context/AppContext";
import { api, mediaSrcById } from "../api";
import { Mic, Check, CheckCheck, Clock } from "lucide-react";

const initials = (name) => (name || "?").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function whenLabel(iso, t) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const day = sameDay(d, now) ? t("today").toLowerCase() : sameDay(d, y) ? t("yesterday").toLowerCase()
    : d.toLocaleDateString([], { day: "numeric", month: "short" });
  return { day, time: d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) };
}

function Section({ icon: Icon, iconClass, title, rows, emptyLabel, testid, t }) {
  return (
    <div className="px-4 pt-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${iconClass || ""}`} /> {title}
        <span className="ml-auto tabular-nums">{rows.length}</span>
      </div>
      <div className="rounded-xl bg-muted/40 divide-y">
        {rows.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground">{emptyLabel}</div>}
        {rows.map((r) => {
          const w = whenLabel(r.at, t);
          return (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2.5" data-testid="message-info-row">
              <div
                className="h-9 w-9 rounded-full text-white text-xs font-bold flex items-center justify-center overflow-hidden shrink-0"
                style={{ backgroundColor: r.avatar_color || "#64748b" }}
              >
                {r.avatar_media_id ? <img src={mediaSrcById(r.avatar_media_id)} alt="" className="h-full w-full object-cover" /> : initials(r.name)}
              </div>
              <div className="text-sm font-medium truncate flex-1 min-w-0">{r.name}</div>
              {w && (
                <div className="text-xs text-muted-foreground tabular-nums shrink-0">
                  <span className="mr-1.5">{w.day}</span><span className="text-foreground/80">{w.time}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** WhatsApp-style "Message info": Played by (voice notes) · Seen by · Delivered to · Pending. */
export function MessageInfoSheet({ open, onClose, message }) {
  const { user, t } = useApp();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!open || !message?.id) return undefined;
    let alive = true;
    setInfo(null); setErr(false);
    api.get(`/messages/${message.id}/info`, { params: { user_id: user.id } })
      .then((r) => { if (alive) setInfo(r.data); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [open, message?.id, user.id]);

  const isVoice = message?.type === "voice";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm p-0 overflow-hidden z-[300]" data-testid="message-info-sheet">
        <DialogHeader className="px-4 pt-4 pb-1">
          <DialogTitle className="font-display text-base">{t("message_info")}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pb-4">
          {!info && !err && <div className="px-4 py-6 text-sm text-muted-foreground" data-testid="message-info-loading">…</div>}
          {err && <div className="px-4 py-6 text-sm text-destructive" data-testid="message-info-error">{t("something_wrong")}</div>}
          {info && (
            <>
              {isVoice && (
                <Section icon={Mic} iconClass="text-primary" title={t("played_by")} rows={info.played_by} emptyLabel={t("nobody_yet")} testid="message-info-played" t={t} />
              )}
              <Section icon={CheckCheck} iconClass="text-sky-500" title={t("seen_by")} rows={info.seen_by} emptyLabel={t("nobody_yet")} testid="message-info-seen" t={t} />
              <Section icon={CheckCheck} title={t("delivered_to")} rows={info.delivered_to} emptyLabel={t("nobody_yet")} testid="message-info-delivered" t={t} />
              {info.pending?.length > 0 && (
                <Section icon={Clock} title={t("not_delivered_yet")} rows={info.pending} emptyLabel="" testid="message-info-pending" t={t} />
              )}
              <div className="px-4 pt-4 text-[11px] text-muted-foreground flex items-center gap-1.5">
                <Check className="h-3 w-3" /> {t("sent_at")} · {(() => { const w = whenLabel(info.message?.created_at, t); return w ? `${w.day} ${w.time}` : ""; })()}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
